import Link from 'next/link'
import { ScanSearch } from 'lucide-react'
import type { SpeciesStripItem } from '@/lib/content-processor'

/**
 * Thumbnail row for the grass-by-state guides, rendered directly after the Fast
 * Answer. One thumbnail + name per grass, each anchored to that grass's section,
 * plus the grass-ID funnel link. Built from the marker payload the content
 * processor emits (insertSpeciesStripMarker); nothing here re-reads the body.
 *
 * Deliberate constraints:
 *  - No heading element: the TOC scrapes h2-h6 from the body and a heading here
 *    would pollute it.
 *  - Thumbnails are NOT zoomable (no data-zoomable): BlogImageLightbox binds
 *    every button[data-zoomable], and clones would add duplicate gallery slides.
 *    The credits section only counts zoomable images, so it is unaffected too.
 *  - Fixed thumbnail box (width/height attrs + aspect ratio) so the row never
 *    shifts layout while images load; lazy, small widths, never priority.
 *  - alt="" on the thumbnails: the visible name is the accessible label.
 */
export const THUMB_WIDTH = 80
export const THUMB_HEIGHT = 60

export function parseSpeciesStripPayload(payload?: string): SpeciesStripItem[] {
  if (!payload) return []
  try {
    const parsed = JSON.parse(payload)
    if (!Array.isArray(parsed)) return []
    return parsed.filter(
      (x): x is SpeciesStripItem =>
        x && typeof x.name === 'string' && typeof x.id === 'string' && typeof x.src === 'string'
    )
  } catch {
    return []
  }
}

export function GrassSpeciesStrip({ payload }: { payload?: string }) {
  const items = parseSpeciesStripPayload(payload)
  if (items.length < 2) return null
  return (
    <nav
      aria-label="Grasses in this guide"
      data-species-strip="true"
      data-module="species_strip"
      className="species-strip ds-glass my-6 md:my-8 rounded-[20px] px-3 py-3 md:px-4 md:py-3.5"
    >
      <ul className="m-0 flex list-none gap-3 overflow-x-auto p-0 pb-1 [scrollbar-width:thin] [scrollbar-color:rgba(22,163,74,0.35)_transparent]">
        {items.map((item) => (
          <li key={item.id} className="m-0 shrink-0 p-0">
            <a
              href={`#${item.id}`}
              className="group flex w-20 flex-col items-center gap-1.5 no-underline"
            >
              <span
                className="block overflow-hidden rounded-xl bg-white/60 ring-1 ring-white/70 shadow-[0_1px_2px_rgba(16,64,32,0.06)] transition-[transform,box-shadow] duration-200 group-hover:-translate-y-0.5 group-hover:shadow-[0_2px_6px_rgba(16,64,32,0.10)]"
                style={{ width: THUMB_WIDTH, height: THUMB_HEIGHT }}
              >
                <img
                  src={`${item.src}?width=${THUMB_WIDTH * 2}&height=${THUMB_HEIGHT * 2}&fit=cover&format=webp`}
                  alt=""
                  width={THUMB_WIDTH}
                  height={THUMB_HEIGHT}
                  loading="lazy"
                  decoding="async"
                  className="block h-full w-full object-cover"
                />
              </span>
              <span className="text-center text-xs font-medium leading-tight text-gray-800 transition-colors group-hover:text-green-700">
                {item.name}
              </span>
            </a>
          </li>
        ))}
        <li className="m-0 flex shrink-0 items-center p-0 pl-1">
          <Link
            href="/what-kind-of-grass-do-i-have"
            className="group flex w-24 flex-col items-center gap-1.5 rounded-xl px-1 py-1 text-center text-xs font-semibold text-green-700 no-underline"
            data-species-strip-cta="true"
          >
            <span
              className="grid place-items-center rounded-xl border border-white/70 bg-green-50/85 text-green-700 shadow-[0_1px_2px_rgba(16,64,32,0.06)] backdrop-blur-md transition-[transform,box-shadow] duration-200 group-hover:-translate-y-0.5 group-hover:shadow-[0_2px_6px_rgba(16,64,32,0.10)]"
              style={{ width: THUMB_WIDTH, height: THUMB_HEIGHT }}
              aria-hidden="true"
            >
              <ScanSearch className="h-6 w-6" aria-hidden="true" />
            </span>
            Not sure? Identify my grass
          </Link>
        </li>
      </ul>
    </nav>
  )
}
